// src/components/recipient/StartCustomizationModal.tsx
import { motion } from "framer-motion";
import {
  Sparkles,
  Heart,
  Gift,
  ArrowRight,
  Stars,
  Flower,
  PartyPopper,
  X,
} from "lucide-react";

interface StartCustomizationModalProps {
  recipient: any;
  onClose: () => void;
  onStart: () => void;
}

export const StartCustomizationModal = ({
  recipient,
  onClose,
  onStart,
}: StartCustomizationModalProps) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="relative w-full max-w-md sm:max-w-2xl lg:max-w-3xl bg-gradient-to-br from-surface/95 to-surface/90 backdrop-blur-2xl rounded-3xl sm:rounded-[48px] border border-white/10 shadow-2xl overflow-hidden"
      >
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 -right-12 sm:-right-20 w-40 sm:w-64 h-40 sm:h-64 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 sm:-bottom-32 -left-12 sm:-left-20 w-64 sm:w-80 h-64 sm:h-80 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 sm:top-6 right-4 sm:right-6 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all"
        >
          <X size={20} className="text-white" />
        </button>

        <div className="relative p-4 sm:p-8 md:p-12">
          {/* Animated icons */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-6 sm:mb-8">
            {[Heart, Sparkles, Flower, PartyPopper].map((Icon, i) => (
              <motion.div
                key={i}
                initial={{ y: 0, opacity: 0.5 }}
                animate={{ y: [-6, 0, -6], opacity: [0.5, 1, 0.5] }}
                transition={{
                  duration: 2.5,
                  delay: i * 0.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center border border-white/20"
              >
                <Icon size={24} className="text-primary sm:text-primary" />
              </motion.div>
            ))}
          </div>

          {/* Main content */}
          <div className="text-center mb-6 sm:mb-8">
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-text-tertiary text-sm sm:text-base md:text-lg max-w-xs sm:max-w-md mx-auto"
            >
              You're about to craft a unique digital experience they'll never
              forget. Let's make it magical.
            </motion.p>
          </div>

          {/* Features grid */}
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-10"
          >
            {[
              { icon: Heart, label: "Personal Message" },
              { icon: Flower, label: "3D Bouquet" },
              { icon: Gift, label: "Custom Gift" },
              { icon: Stars, label: "Animations" },
            ].map((feature, i) => (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-3 sm:p-4 flex flex-col items-center gap-1 sm:gap-2 border border-white/10"
              >
                <feature.icon size={16} className="text-primary" />
                <span className="text-[8px] sm:text-[10px] font-bold uppercase tracking-wider text-white/80 text-center">
                  {feature.label}
                </span>
              </div>
            ))}
          </motion.div>

          {/* Price and CTA */}
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 bg-white/5 backdrop-blur-sm rounded-3xl p-4 sm:p-6 border border-white/10"
          >
            <div className="text-center sm:text-left">
              <p className="text-[9px] sm:text-xs font-bold uppercase tracking-wider text-text-tertiary mb-1">
                Platform Fee
              </p>
              <div className="flex flex-col sm:flex-row items-center sm:items-baseline justify-center sm:justify-start gap-1 sm:gap-2">
                <span className="text-2xl sm:text-3xl font-black text-white">
                  {recipient.price || "KES 1,000"}
                </span>
                <span className="text-[9px] sm:text-sm text-text-tertiary">
                  per experience
                </span>
              </div>
              <p className="text-[8px] sm:text-[10px] text-text-tertiary mt-1 sm:mt-2">
                One-time payment • Unique link • Free updates until sent
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onStart}
              className="group w-full sm:w-auto bg-gradient-to-r from-primary to-secondary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-black flex items-center justify-center gap-2 sm:gap-3 shadow-xl shadow-primary/30"
            >
              <span className="text-[10px] sm:text-sm uppercase tracking-wider">
                Start Creating
              </span>
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 sm:group-hover:translate-x-2 transition-transform"
              />
            </motion.button>
          </motion.div>

          {/* Preview hint */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-[8px] sm:text-[10px] text-text-tertiary uppercase tracking-wider mt-4 sm:mt-6"
          >
            Your design stays private until you publish and pay
          </motion.p>
        </div>
      </motion.div>
    </motion.div>
  );
};
